import React from 'react';
import { FiEye, FiPenTool, FiMonitor, FiCalendar, FiHeart, FiCpu, FiBook } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const WhyChoose = () => {
  const features = [
    {
      icon: FiCpu,
      title: "Robotics Lab",
      description: "State-of-the-art robotics equipment for hands-on STEM learning and innovation, fostering critical thinking and problem-solving skills in young learners.",
      color: "from-blue-400 to-indigo-500",
      image: "/img/why/robotics.jpeg"
    },
    {
      icon: FiBook,
      title: "English / Math Lab", 
      description: "Specialized interactive labs for language development and mathematical concepts, using modern tools and engaging activities to build strong foundations.",
      color: "from-orange-400 to-red-500",
      image: "/img/why/english.jpeg"
    },
    {
      icon: FiBook,
      title: "Library",
      description: "Well-stocked library with diverse collection of books, educational materials, and reading spaces that encourage literacy and a love for learning.",
      color: "from-purple-400 to-pink-500",
      image: "/img/why/library.jpeg"
    },
    {
      icon: FiMonitor,
      title: "Computer Lab",
      description: "Modern computer facilities equipped with age-appropriate software and hardware for digital literacy, coding basics, and technology integration.",
      color: "from-green-400 to-teal-500",
      image: "/img/why/computer.jpeg"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <SafeIcon icon={FiEye} className="w-8 h-8 text-emerald-600" />
            <span className="text-emerald-600 font-semibold text-lg">World-Class Facilities</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            State-of-the-Art Learning{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
              Environments
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our modern facilities are designed to provide the perfect learning environment, 
            equipped with cutting-edge technology and resources that inspire curiosity and innovation.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              data-aos="fade-up"
              data-aos-delay={index * 150}
            >
              <div className="lg:flex">
                {/* Image */}
                <div className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden">
                  <img
                    src={feature.image}
                    alt={`${feature.title} - Premium early childhood education program`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
                  
                  {/* Floating Icon */}
                  <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center`}>
                    <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors duration-200">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {feature.description}
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${feature.color}`}></div>
                      <span className="text-gray-700 text-sm">Expert guidance and supervision</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${feature.color}`}></div>
                      <span className="text-gray-700 text-sm">Age-appropriate activities and materials</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${feature.color}`}></div>
                      <span className="text-gray-700 text-sm">Safe and nurturing environment</span>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className={`w-full h-1 bg-gradient-to-r ${feature.color} rounded-full`}></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-white rounded-3xl p-12 shadow-lg" data-aos="fade-up" data-aos-delay="600">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Give Your Child the Best Start?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join our community of caring families and watch your child flourish in a nurturing, 
            stimulating environment designed for their unique growth and development.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+917416706111"
              className="inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>Schedule a Visit</span>
            </a>
            <a
              href="https://wa.me/917416706111"
              className="inline-flex items-center justify-center space-x-3 bg-green-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-green-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>Chat with Us</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;